import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    AlertTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";

interface ResetCredentialsFormData {
    email: string;
    password: string;
    confirmPassword: string;
    code: string;
}

const StyledTextField = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: "#9e9e9e",
            borderRadius: "5px",
        },
        "& input": {
            paddingLeft: "20px",
        },
    },
});

const ResetCredentials = () => {
    const [formData, setFormData] = useState<ResetCredentialsFormData>({
        email: "",
        password: "",
        confirmPassword: "",
        code: "",
    });
    const [errors, setErrors] = useState<Partial<ResetCredentialsFormData>>({});
    const [tokenError, setTokenError] = useState<string>("");
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        // Get URL parameters without React Router
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const email = urlParams.get("email");

        if (!code) {
            setTokenError(
                "No reset code provided. Please request a new password reset."
            );
            return;
        }

        setFormData((prev) => ({
            ...prev,
            code: code,
            email: email || "",
        }));
    }, []);

    const validateEmail = (email: string) => {
        const validRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
        return validRegex.test(email);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));

        if (name === "email" && value && !validateEmail(value)) {
            setErrors((prev) => ({
                ...prev,
                email: "Please enter a valid email address",
            }));
        }
    };

    const validatePassword = (password: string): boolean => {
        return password.length >= 6;
    };

    function sleep(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const performReset = async () => {
        const newErrors: Partial<ResetCredentialsFormData> = {};

        if (!formData.email || !validateEmail(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }
        if (!formData.password) {
            newErrors.password = "Password is required";
        } else if (!validatePassword(formData.password)) {
            newErrors.password = "Password must be at least 6 characters long";
        }
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }
        if (!formData.code) {
            setTokenError("Reset code is missing");
            return;
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            await axios.post("/api/auth/resetCredentials", {
                email: formData.email,
                password: formData.password,
                code: formData.code,
            });

            setShowSuccess(true);
            // Wait for 3 seconds before closing the dialog
            await sleep(3000);
            closeAndExit();
        } catch (error) {
            console.error(error);
        }
    };

    const handleClose = (event: any, reason: string) => {
        event.preventDefault();

        if (reason === "backdropClick") return;

        closeAndExit();
    };

    function handleCancel(event: any): void {
        event.preventDefault();
        closeAndExit();
    }

    function handleBackToHome(event: any): void {
        event.preventDefault();
        closeAndExit();
    }

    function closeAndExit() {
        // Clear the URL parameters when closing the dialog
        window.history.replaceState({}, "", window.location.pathname);
        window.location.href = "/";
    }

    function handleReset(event: any): void {
        event.preventDefault();
        performReset();
    }

    if (tokenError) {
        return (
            <Dialog open={true} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {tokenError}
                    </Alert>
                    <DialogActions>
                        <Button
                            onClick={handleBackToHome}
                            variant="contained"
                            color="primary"
                        >
                            Back to Home
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            <Dialog open={true} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h4">Reset your password</Typography>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <StyledTextField
                            fullWidth
                            label="Email"
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    color: "black",
                                },
                            }}
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            margin="normal"
                            disabled={!!formData.email}
                            variant="standard"
                        />

                        <StyledTextField
                            fullWidth
                            label="Password"
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    color: "black",
                                },
                            }}
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            margin="normal"
                            variant="standard"
                            required
                        />

                        <StyledTextField
                            fullWidth
                            label="Confirm Password"
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    color: "black",
                                },
                            }}
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            margin="normal"
                            variant="standard"
                            required
                        />

                        {showSuccess &&
                            <Alert severity="success" variant="filled">
                                <AlertTitle>Success</AlertTitle>
                                Password reset successful!
                            </Alert>
                        }
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleCancel}
                        variant="outlined"
                        sx={{
                            bgcolor: "white",
                            height: "41px",
                            width: "90px",
                            p: "5px",
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={handleReset}
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                            height: "41px",
                            "&:focus": {
                                backgroundColor: "#253746 !important",
                            },
                        }}
                    >
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ResetCredentials;
